import { useCallback, useEffect, useRef, useState } from 'react';

const LOOP_DURATION_MS = 45000;
const RESUME_DELAY_MS = 800;
const DRAG_THRESHOLD_PX = 8;

function measureHalfWidth(firstSet, viewport) {
  if (!firstSet || !viewport) return 0;
  const track = firstSet.parentElement;
  const gap = track ? parseFloat(getComputedStyle(track).gap) || 0 : 0;
  return firstSet.offsetWidth + gap;
}

function wrapScroll(viewport, halfWidth, lastScrollLeft) {
  if (!viewport || halfWidth <= 0) return viewport?.scrollLeft ?? 0;
  let { scrollLeft } = viewport;
  if (scrollLeft >= halfWidth) {
    scrollLeft -= halfWidth;
    viewport.scrollLeft = scrollLeft;
  } else if (scrollLeft <= 0 && lastScrollLeft > scrollLeft) {
    scrollLeft += halfWidth;
    viewport.scrollLeft = scrollLeft;
  }
  return viewport.scrollLeft;
}

export function useInfiniteMarquee() {
  const viewportRef = useRef(null);
  const firstSetRef = useRef(null);
  const didDragRef = useRef(false);
  const hoveringRef = useRef(false);
  const focusedRef = useRef(false);
  const pointerDownRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const resumeTimerRef = useRef(null);
  const lastScrollLeftRef = useRef(0);
  const dragStartXRef = useRef(0);
  const lastPointerXRef = useRef(0);
  const mouseDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const isPaused = useCallback(
    () =>
      reducedMotionRef.current ||
      hoveringRef.current ||
      focusedRef.current ||
      pointerDownRef.current,
    [],
  );

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const scheduleResume = useCallback(() => {
    clearResumeTimer();
    resumeTimerRef.current = setTimeout(() => {
      resumeTimerRef.current = null;
    }, RESUME_DELAY_MS);
  }, [clearResumeTimer]);

  const applyDelta = useCallback((delta) => {
    const viewport = viewportRef.current;
    const halfWidth = measureHalfWidth(firstSetRef.current, viewport);
    if (!viewport || halfWidth <= 0 || delta === 0) return;
    let next = viewport.scrollLeft + delta;
    if (next < 0) next += halfWidth;
    if (next >= halfWidth) next -= halfWidth;
    viewport.scrollLeft = next;
    lastScrollLeftRef.current = viewport.scrollLeft;
  }, []);

  const consumeDragClick = useCallback(() => {
    if (!didDragRef.current) return false;
    didDragRef.current = false;
    return true;
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncReducedMotion = () => {
      reducedMotionRef.current = media.matches;
    };
    syncReducedMotion();
    media.addEventListener('change', syncReducedMotion);

    const onScroll = () => {
      const halfWidth = measureHalfWidth(firstSetRef.current, viewport);
      lastScrollLeftRef.current = wrapScroll(viewport, halfWidth, lastScrollLeftRef.current);
    };

    const onPointerDown = (event) => {
      pointerDownRef.current = true;
      didDragRef.current = false;
      mouseDraggingRef.current = false;
      dragStartXRef.current = event.clientX;
      lastPointerXRef.current = event.clientX;
      clearResumeTimer();
    };

    const onPointerMove = (event) => {
      if (!pointerDownRef.current) return;
      const dxFromStart = event.clientX - dragStartXRef.current;
      if (Math.abs(dxFromStart) > DRAG_THRESHOLD_PX) {
        didDragRef.current = true;
      }
      if (event.pointerType !== 'mouse') return;
      if (didDragRef.current && !viewport.hasPointerCapture(event.pointerId)) {
        viewport.setPointerCapture(event.pointerId);
        mouseDraggingRef.current = true;
        setIsDragging(true);
      }
      if (!mouseDraggingRef.current) return;
      const dx = event.clientX - lastPointerXRef.current;
      lastPointerXRef.current = event.clientX;
      applyDelta(-dx);
    };

    const onPointerUp = (event) => {
      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
      pointerDownRef.current = false;
      mouseDraggingRef.current = false;
      setIsDragging(false);
      if (!hoveringRef.current && !focusedRef.current) scheduleResume();
    };

    const onPointerEnter = () => {
      hoveringRef.current = true;
      clearResumeTimer();
    };

    const onPointerLeave = () => {
      hoveringRef.current = false;
      if (!pointerDownRef.current && !focusedRef.current) scheduleResume();
    };

    const onFocusIn = () => {
      focusedRef.current = true;
      clearResumeTimer();
    };

    const onFocusOut = (event) => {
      if (viewport.contains(event.relatedTarget)) return;
      focusedRef.current = false;
      if (!hoveringRef.current && !pointerDownRef.current) scheduleResume();
    };

    const onWheel = (event) => {
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (delta === 0) return;
      event.preventDefault();
      applyDelta(delta);
    };

    const onClickCapture = (event) => {
      if (!didDragRef.current) return;
      event.preventDefault();
      event.stopPropagation();
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('pointerenter', onPointerEnter);
    viewport.addEventListener('pointerleave', onPointerLeave);
    viewport.addEventListener('focusin', onFocusIn);
    viewport.addEventListener('focusout', onFocusOut);
    viewport.addEventListener('click', onClickCapture, true);
    viewport.addEventListener('wheel', onWheel, { passive: false });

    let lastTime = performance.now();
    let frameId = 0;
    const tick = (now) => {
      const dt = now - lastTime;
      lastTime = now;
      const resumePending = resumeTimerRef.current != null;
      if (!isPaused() && !resumePending) {
        const halfWidth = measureHalfWidth(firstSetRef.current, viewport);
        if (halfWidth > 0) {
          viewport.scrollLeft += (halfWidth / LOOP_DURATION_MS) * dt;
          lastScrollLeftRef.current = wrapScroll(viewport, halfWidth, lastScrollLeftRef.current);
        }
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      clearResumeTimer();
      media.removeEventListener('change', syncReducedMotion);
      viewport.removeEventListener('scroll', onScroll);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', onPointerUp);
      viewport.removeEventListener('pointercancel', onPointerUp);
      viewport.removeEventListener('pointerenter', onPointerEnter);
      viewport.removeEventListener('pointerleave', onPointerLeave);
      viewport.removeEventListener('focusin', onFocusIn);
      viewport.removeEventListener('focusout', onFocusOut);
      viewport.removeEventListener('click', onClickCapture, true);
      viewport.removeEventListener('wheel', onWheel);
    };
  }, [applyDelta, clearResumeTimer, isPaused, scheduleResume]);

  return {
    viewportRef,
    firstSetRef,
    isDragging,
    consumeDragClick,
  };
}
